import React, { useEffect, useState } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import { usePageTitle } from '../hooks/usePageTitle'
import { motion } from 'framer-motion'
import { ChevronRightIcon, PencilIcon } from '@heroicons/react/24/outline'
import { useApi } from '../hooks/useApi'
import { api } from '../services/api'
import EventForm from '../components/events/EventForm'
import FormPageSkeleton from '../components/common/FormPageSkeleton'
import ErrorMessage from '../components/common/ErrorMessage'
import { idFromSlug, eventPath } from '../utils/slugs'

export default function EditEventPage() {
  usePageTitle('Edit Event')
  const { id: slug } = useParams()
  const id = idFromSlug(slug)
  const navigate = useNavigate()
  const { execute: updateEvent, loading: updating, error: updateError } = useApi()
  const [localEvent, setEvent] = useState(null)
  const [localLoading, setLoading] = useState(true)
  const [localError, setError] = useState(null)

  useEffect(() => {
    const fetchEvent = async () => {
      try {
        const response = await api.events.get(id)
        setEvent(response)
      } catch (error) {
        setError('Failed to load event')
      } finally {
        setLoading(false)
      }
    }
    fetchEvent()
  }, [id])

  const handleSubmit = async (data) => {
    try {
      await updateEvent(() => api.events.update(id, data))
      navigate(eventPath(localEvent || { name: '', id }))
    } catch (error) {
      // Error state set by useApi, displayed via EventForm's error prop
    }
  }

  if (localLoading) {
    return <FormPageSkeleton />
  }

  if (localError) {
    return (
      <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12">
        <ErrorMessage message={localError} />
      </div>
    )
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
      className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12"
    >
      {/* Breadcrumb */}
      <nav className="flex items-center gap-1.5 text-sm text-text-muted mb-6 overflow-x-auto">
        <Link to="/events" className="link-subtle hover:text-text whitespace-nowrap">Events</Link>
        <ChevronRightIcon className="h-3.5 w-3.5 flex-shrink-0" />
        {localEvent && (
          <>
            <Link to={eventPath(localEvent)} className="link-subtle hover:text-text truncate max-w-[200px]">
              {localEvent.name}
            </Link>
            <ChevronRightIcon className="h-3.5 w-3.5 flex-shrink-0" />
          </>
        )}
        <span className="text-text font-medium whitespace-nowrap">Edit</span>
      </nav>

      {/* Page Header */}
      <div className="flex items-start gap-3.5 mb-8">
        <div className="rounded-xl bg-accent-50 dark:bg-accent-50 p-2.5 flex-shrink-0
          ring-4 ring-accent-50/50 dark:ring-accent-50/30">
          <PencilIcon className="h-6 w-6 text-accent-600 dark:text-accent-500" />
        </div>
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-text leading-tight">Edit Event</h1>
          <p className="text-text-muted mt-1 text-sm sm:text-base">
            {localEvent ? `Update "${localEvent.name}"` : 'Update event details'}
          </p>
        </div>
      </div>

      <EventForm
        initialData={localEvent}
        onSubmit={handleSubmit}
        loading={updating}
        error={updateError}
        submitLabel="Update Event"
        onCancel={() => navigate(eventPath(localEvent || { name: '', id }))}
      />
    </motion.div>
  )
}
